import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Room, RoomVisibility } from "@/types/rooms";

export function useRooms() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRooms = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    setLoading(true);
    const { data } = await supabase
      .from("rooms")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    setRooms((data as Room[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const createRoom = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const { data, error } = await supabase
      .from("rooms")
      .insert([{ user_id: user.id }])
      .select()
      .single();

    if (!error && data) {
      setRooms((prev) => [data as Room, ...prev]);
      return data as Room;
    }
  };

  const renameRoom = async (id: string, name: string) => {
    await supabase.from("rooms").update({ name }).eq("id", id);
    setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, name } : r)));
  };

  const deleteRoom = async (id: string) => {
    await supabase.from("rooms").delete().eq("id", id);
    setRooms((prev) => prev.filter((r) => r.id !== id));
  };

  const changeVisibility = async (id: string, visibility: RoomVisibility) => {
    const { data } = await supabase
      .from("rooms")
      .update({ visibility })
      .eq("id", id)
      .select()
      .single();

    if (data) {
      setRooms((prev) => prev.map((r) => (r.id === id ? (data as Room) : r)));
    }
  };

  return {
    rooms,
    loading,
    fetchRooms,
    createRoom,
    renameRoom,
    deleteRoom,
    changeVisibility,
  };
}
